import { useEffect, useState } from 'react';
import {
  Button,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Modal,
  ModalHeader,
  ModalBody,
} from 'reactstrap';
import { Link, useNavigate } from 'react-router-dom';
import ConfirmationModal from '../Misc/ConfirmationModal'; // Ensure this import is correct
import {
  fetchSharedConfigs,
  fetchUserConfigs,
  fetchUserDetails,
  fetchUserSharedConfigsDetails,
} from './apiUtils';
import ShareTemplateModal from './ShareTemplateModal';
import ModalCustom from 'components/Modals/Modal';

import { convertAccessLevelForFrontend, convertToArray } from './utils';
import NotePreferences from '../../views/settingsViews/NotePreferences';
import OverLayInfoModal from './OverLayInfoModal';
export const useFetchUserAndSharedConfigs = (
  apiClient,
  configID,
  userEmail,
) => {
  // Fetch user configs and shared configs for a user and config ID
  const [sharedAccessData, setSharedAccessData] = useState([]);
  const [viewMode, setViewMode] = useState('');

  // Function to check if a config ID is shared
  const isConfigIDShared = (configID, sharedConfigs) => {
    if (sharedConfigs && sharedConfigs.length > 0) {
      for (const sharedConfig of sharedConfigs) {
        if (sharedConfig.sharedConfigId === configID) {
          setViewMode(convertAccessLevelForFrontend(sharedConfig.access_level));
          return true;
        }
      }
    }
    return false;
  };

  const findSharedAccess = (data, configID) => {
    const foundConfig = data.find((item) => item.configId === configID);
    return foundConfig ? foundConfig.sharedAccess : null;
  };

  useEffect(() => {
    if (userEmail) {
      Promise.all([
        fetchUserConfigs(apiClient),
        fetchSharedConfigs(apiClient, userEmail),
      ])
        .then(([userConfigsData, sharedConfigsData]) => {
          if (isConfigIDShared(configID, sharedConfigsData)) {
            fetchUserSharedConfigsDetails(apiClient, configID, userEmail)
              .then((response) => {
                setSharedAccessData(response);
              })
              .catch((error) => {
                console.error(
                  'Error fetching user shared configs details:',
                  error,
                );
                return [];
              });
          } else {
            const sharedAccessData = findSharedAccess(
              userConfigsData,
              configID,
            );
            const resultArray = sharedAccessData
              ? convertToArray(sharedAccessData)
              : [];
            const sharedData = resultArray.map((config) => ({
              email: config.email,
              access: convertAccessLevelForFrontend(config.access),
            }));
            setSharedAccessData(sharedData);
          }
        })
        .catch((error) => {
          console.log(
            'Error fetching email, configs, or shared configs:',
            error,
          );
        });
    }
  }, [apiClient, configID, userEmail]);

  // Return both sharedAccessData and viewMode as an array or object
  return { sharedAccessData, viewMode };
};

// This Opens the dropdown menu for Template Details, Share Template, and User Preferences
export const OptionsDropdown = ({
  disableEditing,
  apiClient,
  configId,
  userEmail,
  conversationID,
  configEMR,
}) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isPreferencesModalOpen, setIsPreferencesModalOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalText, setModalText] = useState('');
  const [showMapButton, setShowMapButton] = useState(true);
  const [confirmationDetails, setConfirmationDetails] = useState({
    isOpen: false,
    type: '', // 'sample' or 'template'
  });
  const { sharedAccessData, viewMode } = useFetchUserAndSharedConfigs(
    apiClient,
    configId,
    userEmail,
  );
  const [userDetails, setUserDetails] = useState({});

  useEffect(() => {
    // Fetch user details only when the component mounts
    fetchUserDetails(apiClient)
      .then((data) => setUserDetails(data))
      .catch((error) => console.error('Error fetching user details:', error));
  }, [apiClient]);

  const toggleDropdown = () => setDropdownOpen(!dropdownOpen);

  const iconStyle = { color: '#6bd888' };

  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const navigate = useNavigate();

  const handleSettingsClick = () => {
    // setIsSettingsModalOpen(true);
    navigate(`/User/templates/manager#${configId}`);
  };

  const handleDeleteClick = () => {
    // Open the confirmation modal when delete is clicked
    setConfirmationDetails({ isOpen: true, type: 'conversation' }); // Adjust type as needed
  };

  const handleConfirmAction = () => {
    // Call the delete API here
    apiClient
      .deleteConversation(conversationID)
      .then(() => {
        // Handle successful deletion (e.g., show a notification, close modal)
        setConfirmationDetails({ ...confirmationDetails, isOpen: false });
        navigate(`/User/conversations`);
      })
      .catch((error) => {
        console.error('Error deleting the template:', error);
      });
  };

  const handleConfirmClose = () => {
    setIsPreferencesModalOpen(false);
  };

  const resetModalText = () => {
    setModalText(
      "If there are some additional fields you'd like Comprehend to fill out in your EMR for this " +
      'visit type, you can map them now! For this visit specifically you should jump into the fields you want and type $[Section Name]$ in the field and then we will be able to find them! ',
    );
    setShowMapButton(true);
  };
  useEffect(() => {
    resetModalText();
  }, []);

  const mapFields = async () => {
    // Don't show that button after they click it
    setShowMapButton(false);

    // set the modal text to loading...
    setModalText('Loading...');

    // api client map fields
    const newFieldsRaw = await apiClient.mapEMR(conversationID);
    const fields = newFieldsRaw.fields;

    // show the new fields in the modal
    setModalText(
      `The fields have been mapped! Here are the new fields: ${fields.join(', ')}. ` +
      `Now we will extract your 5 most recent notes and train a model on those! (You'll ` +
      ` be able to change these later with the Magic Wand).`,
    );

    // Give them the status from the config in the modal
    // every 5 seconds poll the config and show the status
    const intervalId = setInterval(async () => {
      const statusResponse = await apiClient.getConfigDetails(configId);
      setModalText(`Current status: ${statusResponse.status}`);
      if (statusResponse.status.includes('complete')) {
        clearInterval(intervalId);
        setTimeout(() => {
          window.location.reload();
        }, 5000);
      }
    }, 5000);

    // Give up after 120 seconds
    setTimeout(() => {
      clearInterval(intervalId);
      setModalText('Operation timed out. Please try again.');
    }, 120000);

    // Async kick off the build
    apiClient.emrBuild(configId).then((response) => {
      console.log('emr build response:', response);
      // Show the error and clear the intervalId so it stops refreshing
      if (response.error) {
        setModalText(`Error building the model: ${response.error}`);
        clearInterval(intervalId);
      }
    });

    // Clear interval on component unmount
    return () => clearInterval(intervalId);
  };

  return (
    <>
      {/* Map Modal */}
      <ModalCustom
        isOpen={modalOpen}
        onClose={() => { }}
        buttons={[
          {
            label: 'Close',
            color: 'grey',
            onClick: () => {
              setModalOpen(false);
            },
          },
          showMapButton && {
            label: 'Map Fields',
            color: 'primary',
            onClick: mapFields,
          },
        ].filter(Boolean)}
      >
        <h2>Map New Fields</h2>
        <p>{modalText}</p>
      </ModalCustom>

      <Dropdown isOpen={dropdownOpen} toggle={toggleDropdown}>
        <DropdownToggle
          tag="span"
          aria-expanded={dropdownOpen}
          style={{ display: 'flex', alignItems: 'center', maxWidth: '100%' }}
        >
          <Button
            size="md"
            style={{
              marginBottom: '10px',
              marginTop: '1px',
              marginRight: '5px',
              padding: '10px 15px',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
            title="More Options"
          >
            <i className="fa fa-sliders" style={{ fontSize: '1rem' }}></i>
          </Button>
        </DropdownToggle>

        <DropdownMenu right>
          <DropdownItem onClick={handleSettingsClick}>
            <i className="ni ni-palette text-green" style={iconStyle} />{' '}
            <span className="ml-2">Note Setting Details</span>
          </DropdownItem>

          {!disableEditing && (
            <DropdownItem onClick={() => setIsShareModalOpen(true)}>
              <i className="fa fa-share-nodes" style={iconStyle} />{' '}
              <span className="ml-2">Share Note Setting</span>
            </DropdownItem>
          )}

          <DropdownItem onClick={() => setIsPreferencesModalOpen(true)}>
            <i className="fa fa-cogs" style={iconStyle} />{' '}
            <span className="ml-2">Response Preferences</span>
          </DropdownItem>

          {configEMR === 'Raintree' && (
            <DropdownItem
              onClick={() => {
                resetModalText();
                setModalOpen(true);
              }}
            >
              <i className="fa fa-map" style={iconStyle} />
              <span className="ml-2"> Map More Fields</span>
            </DropdownItem>
          )}

          <DropdownItem onClick={handleDeleteClick}>
            <i className="fa fa-trash" style={{ color: 'black' }} />
            <span className="ml-2"> Delete</span>
          </DropdownItem>
        </DropdownMenu>
      </Dropdown>

      <ShareTemplateModal
        isOpen={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
        configID={configId}
        sharedAccessData={sharedAccessData}
      />
      <OverLayInfoModal
        isOpen={isPreferencesModalOpen}
        onClose={handleConfirmClose}
        buttonText="Save"
        title="Note Generation Preferences"
      >
        {/* Add two new lines (breaks) below the Preferences */}
        <br />
        <div style={{ textAlign: 'center' }}>
          <strong>
            <em>
              Changes will take effect after refreshing the page or starting a
              new conversation.
            </em>
          </strong>
        </div>
        <br />
        <br />
        {/* Pass the children content (NotePreferences component) */}
        <NotePreferences userDetails={userDetails} />
      </OverLayInfoModal>
      {/* Template Settings Modal */}
      <Modal
        isOpen={isSettingsModalOpen}
        toggle={() => setIsSettingsModalOpen(false)}
        size="lg"
      >
        <ModalHeader toggle={() => setIsSettingsModalOpen(false)}>
          {' '}
          {/* Keep built-in close button */}
          Selected Template Details
        </ModalHeader>
        <ModalBody>
          <iframe
            src={`/User/templates/manager#${configId}`}
            style={{ width: '100%', height: '600px', border: 'none' }}
            title="Template Manager"
          />
        </ModalBody>
      </Modal>
      <ConfirmationModal
        isOpen={confirmationDetails.isOpen}
        onClose={() =>
          setConfirmationDetails({ ...confirmationDetails, isOpen: false })
        }
        onConfirm={handleConfirmAction} // Ensure this calls the delete action
      >
        {confirmationDetails.type === 'conversation'
          ? 'Are you sure you want to delete this conversation?'
          : 'Are you sure you want to delete this template?'}
      </ConfirmationModal>
    </>
  );
};
